import type { BaseModelAttributes } from './base';
import type { VideoEditConfig, ExportStatus } from './videoEdit';
import { Resolution, AspectRatio, I18nLabels } from './common';
import { Locale } from './locale';
import { ScriptMode } from './scriptMode';

export enum EpisodeStatus {
  DRAFT = 'draft',
  STORYBOARD_GENERATION = 'storyboard_generation',
  IMAGE_GENERATION = 'image_generation',
  VOICE_GENERATION = 'voice_generation',
  // SOUND_GENERATION = 'sound_generation',
  VIDEO_GENERATION = 'video_generation',
  COMPLETED = 'completed'
}

export const EpisodeStatusLabels: I18nLabels<EpisodeStatus> = {
  [EpisodeStatus.DRAFT]: { [Locale.ZH_CN]: '草稿', [Locale.EN_US]: 'Draft' },
  [EpisodeStatus.STORYBOARD_GENERATION]: { [Locale.ZH_CN]: '分镜生成', [Locale.EN_US]: 'Storyboard Gen' },
  [EpisodeStatus.IMAGE_GENERATION]: { [Locale.ZH_CN]: '图片生成', [Locale.EN_US]: 'Image Gen' },
  [EpisodeStatus.VOICE_GENERATION]: { [Locale.ZH_CN]: '配音生成', [Locale.EN_US]: 'Voice Gen' },
  // [EpisodeStatus.SOUND_GENERATION]: { [Locale.ZH_CN]: '音效生成', [Locale.EN_US]: 'Sound Gen' },
  [EpisodeStatus.VIDEO_GENERATION]: { [Locale.ZH_CN]: '视频生成', [Locale.EN_US]: 'Video Gen' },
  [EpisodeStatus.COMPLETED]: { [Locale.ZH_CN]: '已完成', [Locale.EN_US]: 'Completed' },
};

export interface IEpisode extends BaseModelAttributes {
  projectId?: string;
  userId: string;
  episodeNumber?: number;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  status: EpisodeStatus;
  activeScriptId?: string;  // 当前使用的剧本ID（已废弃，使用 scriptId）
  scriptId?: string;  // 新增：直接关联到唯一的 EpisodeScript（1:1 关系）
  batchResourceJobId?: string;  // 批量资源生成任务ID
  batchVideoJobId?: string;  // 批量视频生成任务ID
  // 默认模型选择
  llmModelId?: string;
  llmVlModelId?: string;
  text2imageModelId?: string;
  videoModelId?: string;
  image2videoModelId?: string;
  text2videoModelId?: string;
  keyframe2videoModelId?: string;
  multiImage2videoModelId?: string;
  imageEditModelId?: string;
  text2speechModelId?: string;
  videoLipsyncModelId?: string;
  // 视频定义属性
  resolution?: Resolution;
  videoAspectRatio?: AspectRatio;
  videoStyle?: string;
  // 脚本模式
  scriptMode?: ScriptMode;   // 视频生成模式（与 EpisodeScript.scriptMode 同步）
  // 分镜配置
  maxShotDuration?: number;  // 单个分镜最大时长（秒），NULL 表示不限制
  // 音频资源
  narratorVoiceUrl?: string;  // 旁白音色URL
  bgmUrl?: string;           // 背景音乐URL
  // 视频编辑配置
  editConfig?: VideoEditConfig;
  // 最终导出的视频
  finalVideoUrl?: string;
  exportStatus?: ExportStatus;
  exportProgress?: number; // 0-100
  exportJobId?: string;
  project?: {
    id: string;
    name: string;
  };
}

export interface CreateEpisodeRequest {
  projectId?: string;
  episodeNumber?: number;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  status?: EpisodeStatus;
  activeScriptId?: string;
  scriptId?: string;
  // 默认模型选择
  llmModelId?: string;
  llmVlModelId?: string;
  text2imageModelId?: string;
  videoModelId?: string;
  image2videoModelId?: string;
  text2videoModelId?: string;
  keyframe2videoModelId?: string;
  multiImage2videoModelId?: string;
  imageEditModelId?: string;
  text2speechModelId?: string;
  videoLipsyncModelId?: string;
  // 视频定义属性
  resolution?: Resolution;
  videoAspectRatio?: AspectRatio;
  videoStyle?: string;
  // 音频资源
  narratorVoiceUrl?: string;
  bgmUrl?: string;
}

export interface UpdateEpisodeRequest {
  episodeNumber?: number;
  title?: string;
  description?: string;
  thumbnailUrl?: string;
  status?: EpisodeStatus;
  activeScriptId?: string;
  scriptId?: string;
  // 默认模型选择
  llmModelId?: string;
  llmVlModelId?: string;
  text2imageModelId?: string;
  videoModelId?: string;
  image2videoModelId?: string;
  text2videoModelId?: string;
  keyframe2videoModelId?: string;
  multiImage2videoModelId?: string;
  imageEditModelId?: string;
  text2speechModelId?: string;
  videoLipsyncModelId?: string;
  // 视频定义属性
  resolution?: Resolution;
  videoAspectRatio?: AspectRatio;
  videoStyle?: string;
  // 音频资源
  narratorVoiceUrl?: string;
  bgmUrl?: string;
}

export interface BatchGenerateResourcesRequest {
  text2imageModelId?: string;
  imageEditModelId?: string;
  text2speechModelId?: string;
  videoAspectRatio?: AspectRatio;
  generateEndFrame?: boolean;
  /**
   * 是否生成图片（起始帧 / 结束帧）
   * - 默认 true：为每个分镜生成起始帧（以及可选的结束帧）
   * - false：跳过图片生成，仅处理音频等其他资源；此时 generateEndFrame 必须也为 false
   */
  generateImages?: boolean;
}

export interface BatchGenerateVideosRequest {
  text2videoModelId?: string;
  image2videoModelId?: string;
  keyframe2videoModelId?: string;
}

export interface EpisodeQuery {
  search?: string;
  projectId?: string;
  status?: EpisodeStatus;
}
